<template>
  <div>
    <button class="btn-svg !max-w-[30px] !shadow-none bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline" @click="openModal">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="!h-6 !w-6">
        <path d="M290.7 93.2l128 128-278 278-114.1 12.6C11.4 513.5-1.6 500.6 .1 485.3l12.7-114.2 277.9-277.9zm207.2-19.1l-60.1-60.1c-18.8-18.8-49.2-18.8-67.9 0l-56.6 56.6 128 128 56.6-56.6c18.8-18.8 18.8-49.2 0-67.9z"/>
      </svg>
    </button>
    <div v-if="isModalOpen" class="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center">
      <div class="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg relative" @click.stop>
        <button @click="closeModal" class="absolute top-2 right-2 text-gray-600 hover:text-gray-900 text-lg leading-none">
          &times;
        </button>
        <h2 class="text-xl font-bold">{{ uiT('groups.edit') }}</h2>
        <form @submit.prevent="updateGroup" class="mt-4">
          <div class="mb-4">
            <label for="groupName" class="block text-gray-700 font-medium">{{ uiT('groups.name') }}</label>
            <input id="groupName" v-model="groupName" type="text" required class="mt-1 w-full border border-gray-300 rounded-md shadow-sm px-4 py-2">
          </div>

          <template v-if="sinLimitesCargaUsuarios">
            <div class="mode-card mb-5">
              <div class="mode-title">{{ uiT('groups.visibilityTitle') }}</div>
              <div class="mode-sub">{{ uiT('groups.visibilityHelp') }}</div>
            </div>
            <div class="space-y-3 mb-5">
              <label class="toggle-row">
                <span>{{ normalizedLabelStaff }}</span>
                <span class="switch-control">
                  <input v-model="mostrarStaff" type="checkbox" class="switch-checkbox" />
                  <span class="switch-ui"><span class="switch-knob"></span></span>
                </span>
              </label>
              <label class="toggle-row">
                <span>{{ normalizedLabelExpositor }}</span>
                <span class="switch-control">
                  <input v-model="mostrarConferencistas" type="checkbox" class="switch-checkbox" />
                  <span class="switch-ui"><span class="switch-knob"></span></span>
                </span>
              </label>
              <label class="toggle-row">
                <span>{{ normalizedLabelAsistentes }}</span>
                <span class="switch-control">
                  <input v-model="mostrarAsistentes" type="checkbox" class="switch-checkbox" />
                  <span class="switch-ui"><span class="switch-knob"></span></span>
                </span>
              </label>
            </div>
          </template>

          <div v-else class="grid grid-cols-3 gap-4 mb-5">
            <div>
              <label for="staff" class="block text-gray-700 font-medium">{{ normalizedLabelStaff }}:</label>
              <input id="staff" :value="staff" @input="updateValue('staff', $event.target.value)" type="number" min="0" class="mt-1 w-full border border-gray-300 rounded-md shadow-sm px-4 py-2">
            </div>
            <div>
              <label for="conferencistas" class="block text-gray-700 font-medium">{{ normalizedLabelExpositor }}:</label>
              <input id="conferencistas" :value="conferencistas" @input="updateValue('conferencistas', $event.target.value)" type="number" min="0" class="mt-1 w-full border border-gray-300 rounded-md shadow-sm px-4 py-2">
            </div>
            <div>
              <label for="asistentes" class="block text-gray-700 font-medium">{{ normalizedLabelAsistentes }}:</label>
              <input id="asistentes" :value="asistentes" @input="updateValue('asistentes', $event.target.value)" type="number" min="0" class="mt-1 w-full border border-gray-300 rounded-md shadow-sm px-4 py-2">
            </div>
          </div>
          <div class="flex justify-between space-x-4">
            <button type="submit" class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">{{ uiT('common.update') }}</button>
            <button type="button" @click="confirmDelete" :class="{'bg-red-500 hover:bg-red-700': canDelete, 'bg-gray-400 cursor-not-allowed': !canDelete}" class="text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">{{ uiT('groups.delete') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import axios from 'axios';
import { userManagementI18nMixin } from "../i18n/userManagementI18n.js";

const normalizeLabel = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  const clean = value.trim();
  return clean.length ? clean : fallback;
};

export default {
  mixins: [userManagementI18nMixin],
  setup() {
    const toast = useToast();
    return { toast };
  },
  props: {
    group: Object,
    staff: Number,
    conferencistas: Number,
    asistentes: Number,
    labelStaff: { type: String, default: '' },
    labelAsistentes: { type: String, default: '' },
    labelExpositor: { type: String, default: '' },
    sinLimitesCargaUsuarios: { type: Boolean, default: false }
  },
  data() {
    return {
      isModalOpen: false,
      groupName: '',
      canDelete: false,
      mostrarStaff: false,
      mostrarAsistentes: false,
      mostrarConferencistas: false,
    };
  },
  computed: {
    normalizedLabelStaff() {
      return this.uiCategoryLabel(normalizeLabel(this.labelStaff, ''), 'staff');
    },
    normalizedLabelAsistentes() {
      return this.uiCategoryLabel(normalizeLabel(this.labelAsistentes, ''), 'asistentes');
    },
    normalizedLabelExpositor() {
      return this.uiCategoryLabel(normalizeLabel(this.labelExpositor, ''), 'conferencistas');
    }
  },
  methods: {
    openModal() {
      this.isModalOpen = true;
      this.groupName = this.group.group_name;
      this.canDelete = Array.isArray(this.group.users) ? this.group.users.length === 0 : false;
      this.mostrarStaff = String(this.group.mostrar_staff || '0') === '1';
      this.mostrarAsistentes = String(this.group.mostrar_asistentes || '0') === '1';
      this.mostrarConferencistas = String(this.group.mostrar_conferencistas || '0') === '1';
    },
    closeModal() {
      this.isModalOpen = false;
    },
    updateValue(field, value) {
      this.$emit(`update:${field}`, Number(value));
    },
    updateGroup() {
      const nextStaff = this.sinLimitesCargaUsuarios ? Number(this.group.staff ?? 0) : Number(this.staff);
      const nextAsistentes = this.sinLimitesCargaUsuarios ? Number(this.group.asistentes ?? 0) : Number(this.asistentes);
      const nextConferencistas = this.sinLimitesCargaUsuarios ? Number(this.group.conferencistas ?? 0) : Number(this.conferencistas);
      const mostrarStaff = this.sinLimitesCargaUsuarios ? (this.mostrarStaff ? '1' : '0') : (nextStaff > 0 ? '1' : '0');
      const mostrarAsistentes = this.sinLimitesCargaUsuarios ? (this.mostrarAsistentes ? '1' : '0') : (nextAsistentes > 0 ? '1' : '0');
      const mostrarConferencistas = this.sinLimitesCargaUsuarios ? (this.mostrarConferencistas ? '1' : '0') : (nextConferencistas > 0 ? '1' : '0');

      axios.post(`/api/groups/update`, {
        name: this.groupName,
        tokenGroup: this.group.group_id,
        cantStaff: String(nextStaff),
        cantAsistente: String(nextAsistentes),
        cantConferencista: String(nextConferencistas),
        mostrarStaff,
        mostrarAsistentes,
        mostrarConferencistas,
      }).then(response => {
        if (response.data.success) {
          this.$emit('groupUpdated', {
            ...this.group,
            group_name: this.groupName,
            staff: nextStaff,
            asistentes: nextAsistentes,
            conferencistas: nextConferencistas,
            mostrar_staff: mostrarStaff,
            mostrar_asistentes: mostrarAsistentes,
            mostrar_conferencistas: mostrarConferencistas
          });
          this.closeModal();
        }
      }).catch(error => {
        console.error('Error updating group:', error);
      });
    },
    confirmDelete() {
      if (!this.canDelete) return;
      if (confirm(this.uiT('groups.confirmDelete'))) {
        axios.post(`/api/groups/delete/${this.group.group_id}`).then(response => {
          if (response.data.success) {
            this.$emit('groupDeleted', this.group.group_id);
            this.toast.success(this.uiT('groups.deleted'));
            this.closeModal();
          }
        }).catch(error => {
          console.error('Error deleting group:', error);
        });
      }
    }
  }
};
</script>

<style scoped>
.btn-svg {
  display:flex;
  justify-content:center;
  align-items:center;
}
.btn-svg svg {
  min-width: 15px;
  fill: #fff;
}
.bg-opacity-40{ z-index: 1; }
.mode-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #f8fafc;
  padding: 14px;
}
.mode-title { font-weight: 700; color: #0f172a; }
.mode-sub { margin-top: 6px; font-size: 0.9rem; color: #64748b; }
.toggle-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

.switch-control {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.switch-checkbox {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.switch-ui {
  width: 52px;
  height: 30px;
  border-radius: 999px;
  background: #cbd5e1;
  display: inline-flex;
  align-items: center;
  padding: 4px;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
}
.switch-knob {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.25);
  transition: transform 0.2s ease;
}
.switch-checkbox:checked + .switch-ui {
  background: #22c55e;
}
.switch-checkbox:checked + .switch-ui .switch-knob {
  transform: translateX(22px);
}
.switch-checkbox:focus-visible + .switch-ui {
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.22);
}
</style>
